import React, { useEffect } from "react";
import withStyles from "@material-ui/core/styles/withStyles";
import createStyles from "@material-ui/core/styles/createStyles";
import { Formik, Field } from "formik";
import * as Yup from "yup";
import {
  WithStyles,
  Paper,
  Typography,
  Button,
  InputLabel
} from "@material-ui/core";
import { TextField } from "formik-material-ui";
import {
  FormikSubmitDispatchProps,
  BusinessAreaForm,
  BusinessArea,
  SelectFieldOption,
  TabComponentProps
} from "../../../redux/types";
import { Link, LinkProps } from "react-router-dom";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import { AutoCompleteSelect } from "../../AutoCompleteSelect";
import { useTranslate } from "../../../services/appLanguageService";
import store from "../../../redux/store";
import { setTitle } from "../../../redux/reducers/tabRouter/tabTitle/actions";

let ValidationSchema = (t: any) =>
  Yup.object().shape({
    name: Yup.string()
      .min(1, t("minLengthError"))
      .max(255, t("max255LengthError"))
      .required(t("requiredError")),
    notes: Yup.string().nullable().notRequired(),
    invoicingprofile: Yup.number().nullable().notRequired()
  });

const styles = (theme: any) =>
  createStyles({
    form: {
      width: "100%", // Fix IE 11 issue.
      marginTop: theme.spacing(1)
    },
    submit: {
      marginTop: theme.spacing(3)
    },
    paper: {
      padding: 20,
      marginTop: 20
    },
    selectLabel: {
      marginTop: theme.spacing(3)
    },
    select: {
      marginTop: theme.spacing(1)
    }
  });

interface NewBusinessAreaProps
  extends FormikSubmitDispatchProps<BusinessAreaForm>,
    WithStyles<typeof styles>,
    TabComponentProps {
  businessArea?: BusinessArea;
  invoicingProfileOptions: SelectFieldOption[];
  contractingProfileOptions: SelectFieldOption[];
  loadContractingProfiles: (searchTerm?: string) => void;
  loadInvoicingProfiles: (searchTerm?: string) => void;
}

const NewEditBusinessArea: React.FC<NewBusinessAreaProps> = (props) => {
  const {
    classes,
    businessArea,
    invoicingProfileOptions,
    contractingProfileOptions,
    loadContractingProfiles,
    loadInvoicingProfiles,
    tabId
  } = props;
  const t = useTranslate("BusinessAreaPage");
  const t3 = useTranslate("ValidationErrorMessages");
  let initialValues: BusinessArea | BusinessAreaForm;
  if (businessArea && (businessArea as BusinessArea).id) {
    initialValues = {
      ...businessArea
    };
  } else {
    initialValues = {
      id: -1,
      name: "",
      notes: "",
      invoicingprofile: null,
      contractingprofile: null
    };
  }
  useEffect(() => {
    if (businessArea) {
      store.dispatch(setTitle(businessArea.name, tabId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessArea]);

  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        component={React.forwardRef<HTMLAnchorElement, Partial<LinkProps>>(
          (props, ref) => (
            <Link
              to={`/contracts/businessareas${
                businessArea ? `/${businessArea.id}` : ""
              }?same_tab=true`}
              {...props}
              ref={ref as any}
            />
          )
        )}
      >
        <NavigateBeforeIcon /> {t("navigateBeforeIconLabel")}
      </Button>
      <Paper className={classes.paper}>
        <Typography component="h1" variant="h6">
          {businessArea
            ? t("editBusinessAreaHeading")
            : t("newBusinessAreaHeading")}
        </Typography>
        <Formik
          initialValues={initialValues}
          validationSchema={ValidationSchema(t3)}
          onSubmit={props.handleSubmit}
        >
          {(props) => (
            <form
              className={classes.form}
              onSubmit={props.handleSubmit}
              autoComplete="off"
            >
              <Field
                id="businessarea-form-name"
                type="text"
                name="name"
                label={t("nameLabel")}
                placeholder={t("nameLabel")}
                component={TextField}
                margin="normal"
                fullWidth
              />

              <InputLabel
                className={classes.selectLabel}
                htmlFor="form-invoicingprofile"
              >
                {t("invoicingProfileLabel")}
              </InputLabel>
              <Field
                id="form-invoicingprofile"
                type="text"
                name="invoicingprofile"
                label={t("invoicingProfileLabel")}
                placeholder={t("invoicingProfileLabel")}
                value={
                  invoicingProfileOptions.find(
                    (invoicingProfile) =>
                      props.values.invoicingprofile === invoicingProfile.value
                  )
                    ? invoicingProfileOptions.find(
                        (invoicingProfile) =>
                          props.values.invoicingprofile ===
                          invoicingProfile.value
                      )
                    : null
                }
                options={invoicingProfileOptions}
                component={AutoCompleteSelect}
                margin="normal"
                fullWidth
                loadOptions={loadInvoicingProfiles}
              />

              <InputLabel
                className={classes.selectLabel}
                htmlFor="form-contractingprofile"
              >
                {t("contractingProfileLabel")}
              </InputLabel>
              <Field
                id="form-contractingprofile"
                type="text"
                name="contractingprofile"
                label={t("contractingProfileLabel")}
                placeholder={t("contractingProfileLabel")}
                value={
                  contractingProfileOptions.find(
                    (contractingProfile) =>
                      props.values.contractingprofile ===
                      contractingProfile.value
                  )
                    ? contractingProfileOptions.find(
                        (contractingProfile) =>
                          props.values.contractingprofile ===
                          contractingProfile.value
                      )
                    : null
                }
                options={contractingProfileOptions}
                component={AutoCompleteSelect}
                margin="normal"
                fullWidth
                loadOptions={loadContractingProfiles}
              />

              <Field
                id="new-businessarea-form-notes"
                type="text"
                name="notes"
                label={t("notesLabel")}
                placeholder={t("notesLabel")}
                component={TextField}
                multiline={true}
                margin="normal"
                rows="4"
                fullWidth
              />

              <Button
                id="new-businessarea-form-submit"
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
              >
                {businessArea ? t("saveButtonLabel") : t("createButtonLabel")}
              </Button>
            </form>
          )}
        </Formik>
      </Paper>
    </div>
  );
};

export default withStyles(styles)(NewEditBusinessArea);
